/** BIR Form 2307 issued (M05-08): one certificate per supplier per quarter. */
import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { FileDown } from 'lucide-react';
import { api } from '../../lib/api';
import { amount } from '../../lib/money';
import { openFile } from '../../lib/files';
import { manilaToday } from '../../lib/queries';
import { Alert, Button, Card, EmptyState, FilterBar, Money, PageHeader, Skeleton, TableWrap, Td, Th, filterClass } from '../../components/ui';

interface Row {
  contactId: string;
  supplier: string;
  email: string;
  incomeAmount: string;
  taxWithheld: string;
}

export function Wht2307Page(): JSX.Element {
  const today = manilaToday();
  const [year, setYear] = useState(Number(today.slice(0, 4)));
  const [quarter, setQuarter] = useState(Math.floor((Number(today.slice(5, 7)) - 1) / 3) + 1);
  const [error, setError] = useState<string | null>(null);
  const list = useQuery({ queryKey: ['2307-issued', year, quarter], queryFn: () => api<Row[]>(`/wht-certificates/issued?year=${year}&quarter=${quarter}`) });
  const open = useMutation({ mutationFn: (contactId: string) => openFile(`/wht-certificates/issued/${contactId}/pdf?year=${year}&quarter=${quarter}`), onError: (e) => setError((e as Error).message) });
  return (
    <>
      <PageHeader title="2307 Certificates Issued" description="Every supplier you withheld tax from this quarter. Each certificate is built only from posted bills and payments." />
      <FilterBar>
        <select className={filterClass} value={year} onChange={(e) => setYear(Number(e.target.value))} aria-label="Year">
          {[0, 1, 2].map((d) => Number(today.slice(0, 4)) - d).map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        <select className={filterClass} value={quarter} onChange={(e) => setQuarter(Number(e.target.value))} aria-label="Quarter">
          {[1, 2, 3, 4].map((q) => <option key={q} value={q}>Q{q}</option>)}
        </select>
      </FilterBar>
      {error && <div className="mb-4"><Alert tone="critical">{error}</Alert></div>}
      <Card>
        {list.isLoading && <Skeleton className="h-40 w-full" />}
        {list.data?.length === 0 && <EmptyState title="No withholding this quarter" />}
        {list.data && list.data.length > 0 && (
          <TableWrap>
            <thead><tr className="border-b border-slate-200 dark:border-slate-800"><Th>Supplier</Th><Th align="right">Income payments</Th><Th align="right">Tax withheld</Th><Th>{''}</Th></tr></thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {list.data.map((r) => (
                <tr key={r.contactId}>
                  <Td className="font-medium">{r.supplier}{r.email && <span className="block text-xs text-slate-500">{r.email}</span>}</Td>
                  <Td align="right"><Money>{amount(r.incomeAmount)}</Money></Td>
                  <Td align="right"><Money className="font-semibold">{amount(r.taxWithheld)}</Money></Td>
                  <Td align="right"><Button size="sm" variant="secondary" busy={open.isPending && open.variables === r.contactId} onClick={() => open.mutate(r.contactId)}><FileDown aria-hidden="true" className="h-4 w-4" /> 2307 PDF</Button></Td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
        )}
      </Card>
    </>
  );
}
